// src/services/realtimeService.ts
import { io, Socket } from 'socket.io-client';
import { store } from '../store';
import { addNotification } from '../store/slices/notificationsSlice';
import { sendMessage } from '../store/slices/chatSlice';
import { socketAppointmentUpdated } from '../store/slices/appointmentsSlice';
import { Notification } from '../store/slices/notificationsSlice';
import { Message } from '../store/slices/chatSlice';

type AppointmentUpdateListener = (data: any) => void;

class RealtimeService {
  private socket: Socket | null = null;
  private connected: boolean = false;
  private appointmentListeners = new Set<AppointmentUpdateListener>();

  // Initialize the WebSocket connection
  init(token: string) {
    if (this.socket) {
      this.socket.disconnect();
    }

    // Use REACT_APP_SOCKET_URL if set (Vercel), otherwise construct from API URL or default
    const socketUrl = process.env.REACT_APP_SOCKET_URL 
      || (process.env.REACT_APP_API_BASE_URL 
          ? process.env.REACT_APP_API_BASE_URL.replace('/api', '')
          : (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : 'http://localhost:5000'));
    
    this.socket = io(socketUrl, {
      transports: ['websocket'],
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    // Connection events
    this.socket.on('connect', this.handleConnect);
    this.socket.on('disconnect', this.handleDisconnect);
    this.socket.on('connect_error', this.handleConnectError);

    // Application events
    this.socket.on('notification', this.handleNotification);
    this.socket.on('message', this.handleMessage);
    this.socket.on('appointment_update', this.handleAppointmentUpdate);
  }

  // Disconnect and clean up
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  // Connection handlers
  private handleConnect = () => {
    console.log('Connected to realtime service');
    this.connected = true;
  };

  private handleDisconnect = (reason: string) => {
    console.log('Disconnected from realtime service:', reason);
    this.connected = false;
  };

  private handleConnectError = (error: Error) => {
    console.error('Connection error:', error);
    this.connected = false;
  };

  // Message handlers
  private handleNotification = (notification: Notification) => {
    // Dispatch to Redux store
    store.dispatch(addNotification(notification));
  };

  private handleMessage = (message: Message) => {
    // In a real app, we'd check if the message is from the current user
    // and if not, dispatch it to the store
    const state = store.getState();
    const currentUserId = state.auth.user?.id;

    if (message.senderId !== currentUserId) {
      // It's a message from someone else, add it to the store
      store.dispatch(sendMessage({
        threadId: message.threadId,
        content: message.content,
        senderId: message.senderId,
        senderName: message.senderName,
        senderRole: message.senderRole,
        senderPhoto: message.senderPhoto
      }));
    }
  };

  private handleAppointmentUpdate = (data: any) => {
    console.log('[RealtimeService] Appointment updated via socket:', data?.appointmentId, '→', data?.status);
    if (data?.appointment) {
      store.dispatch(socketAppointmentUpdated(data.appointment));
    }
    this.appointmentListeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error('[RealtimeService] Appointment listener failed:', error);
      }
    });
  };

  onAppointmentUpdate(listener: AppointmentUpdateListener) {
    this.appointmentListeners.add(listener);
    return () => {
      this.appointmentListeners.delete(listener);
    };
  }

  // Send a chat message
  sendChatMessage(threadId: string | null, content: string) {
    if (!this.connected || !this.socket) {
      return Promise.reject(new Error('Not connected to realtime service'));
    }

    if (!threadId) {
      return Promise.reject(new Error('Thread ID is required to send a message'));
    }

    return new Promise((resolve, reject) => {
      this.socket!.emit('send_message', { threadId, content }, (response: any) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }

  // Check if connected
  isConnected(): boolean {
    return this.connected;
  }
}

export const realtimeService = new RealtimeService();
export default realtimeService;